import type { HazardDef, LevelDef, SwitchDef, WorldState } from '../../shared/types';
import { isHazardActive } from '../../shared/simulation';

export interface HazardPresentation {
  active: boolean;
  held: boolean;
  keyLocked: boolean;
  warning: boolean;
  seconds: number | null;
  progress: number;
  letters: string;
  label: string;
  accessible: string;
}

/** Labels describe the shared engine's hazard state; they never grant safe passage. */
export function describeHazard(hazard: HazardDef, level: LevelDef, world: WorldState): HazardPresentation {
  const active = isHazardActive(hazard, world);
  const held = !!hazard.disabledBy?.length && hazard.disabledBy.every(id => world.switches.some(plate => plate.id === id && plate.active));
  const keyLocked = !!hazard.cycleAfterKey && world.collectedKeys.length === 0;
  const letters = (hazard.disabledBy ?? []).map(id => {
    const index = level.switches.findIndex(plate => plate.id === id);
    return index >= 0 ? String.fromCharCode(65 + index) : '?';
  }).join('+');
  const cycle = hazard.cycle;
  const phase = cycle && cycle.period > 0 ? ((world.elapsed + cycle.phase) % cycle.period + cycle.period) % cycle.period : 0;
  const cycling = !!cycle && cycle.period > 0 && cycle.on > 0 && cycle.on < cycle.period && !keyLocked && !held;
  const warning = cycling && !active && cycle.period - phase < 0.65;
  const seconds = cycling ? Math.max(0, active ? cycle.on - phase : cycle.period - phase) : null;
  const label = held ? `HELD · ${letters || 'SHIELD'}` : keyLocked ? letters ? `HOLD ${letters}` : 'KEY LOCKED'
    : `${active ? 'WAIT' : warning ? 'SOON' : 'GO'}${seconds === null ? '' : ` · ${seconds.toFixed(1)}s`}`;
  const accessible = held ? `The field is protected by plate ${letters}.`
    : keyLocked ? `Hold plate ${letters || 'the shelter'} to protect the crossing. This beam cannot cycle off until the team finds a key.`
      : `${active ? 'Hazard active: wait' : warning ? 'Hazard returns soon' : 'Hazard clear: go'}${seconds === null ? '' : `, ${seconds.toFixed(1)} seconds`}.`;
  return { active, held, keyLocked, warning, seconds, progress: cycling ? phase / cycle.period : 0, letters, label, accessible };
}

/** The solid conduit's polyline (world px) from a protecting plate to the held beam's roof. */
export function projectorPath(plate: SwitchDef, hazard: HazardDef): { x: number; y: number }[] {
  const sx = plate.x + plate.w / 2, sy = plate.y + plate.h + 9;
  const elbow = hazard.x - 10;
  return [{ x: sx, y: sy }, { x: elbow, y: sy }, { x: elbow, y: hazard.y + 18 }, { x: hazard.x + 7, y: hazard.y + 18 }];
}

/** A persistent projected canopy. The same actual plate lights the conduit and the
 * receiving field. Incoming beam threads stop on its roof while the volume is safe. */
export function drawProtectionProjectors(ctx: CanvasRenderingContext2D, level: LevelDef, world: WorldState): void {
  for (const hazard of level.hazards) {
    const state = describeHazard(hazard, level, world);
    if (!state.held || hazard.kind !== 'beam') continue;
    const { x, y, w, h } = hazard;
    ctx.save();
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    for (const id of hazard.disabledBy ?? []) {
      const plate = level.switches.find(candidate => candidate.id === id);
      if (!plate) continue;
      const [start, ...rest] = projectorPath(plate, hazard);
      ctx.beginPath(); ctx.moveTo(start.x, start.y); for (const point of rest) ctx.lineTo(point.x, point.y);
      ctx.strokeStyle = '#31584b'; ctx.lineWidth = 6; ctx.stroke();
      ctx.strokeStyle = '#c0dba3'; ctx.lineWidth = 3.8; ctx.stroke();
      ctx.strokeStyle = '#ffdda0'; ctx.lineWidth = 1.3; ctx.stroke();
      ctx.beginPath(); ctx.arc(start.x, start.y, 5, 0, Math.PI * 2); ctx.fillStyle = '#f2c267'; ctx.fill();
      ctx.strokeStyle = '#293f39'; ctx.lineWidth = 1.8; ctx.stroke();
    }
    ctx.beginPath();
    ctx.moveTo(x + 3, y + 21);
    ctx.quadraticCurveTo(x + w / 2, y - 12, x + w - 3, y + 21);
    ctx.lineTo(x + w - 3, y + 28);
    ctx.quadraticCurveTo(x + w / 2, y - 3, x + 3, y + 28);
    ctx.closePath();
    ctx.fillStyle = '#b8d7a1'; ctx.fill();
    ctx.strokeStyle = '#355a4b'; ctx.lineWidth = 2.4; ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x + 5, y + 19); ctx.quadraticCurveTo(x + w / 2, y - 10, x + w - 5, y + 19);
    ctx.strokeStyle = '#eff1c7'; ctx.lineWidth = 2; ctx.stroke();
    // The protected volume is transparent; its sides are light, not solid platforms.
    ctx.beginPath(); ctx.moveTo(x + 4, y + 28); ctx.lineTo(x + 4, y + h - 2);
    ctx.moveTo(x + w - 4, y + 28); ctx.lineTo(x + w - 4, y + h - 2);
    ctx.strokeStyle = 'rgba(184,215,161,.55)'; ctx.lineWidth = 2; ctx.stroke();
    for (let px = x + 15; px < x + w - 5; px += 27) {
      const unit = (px - x) / w;
      const roofY = y + 20 - Math.sin(unit * Math.PI) * 15;
      ctx.beginPath(); ctx.moveTo(px, y); ctx.lineTo(px, roofY - 2);
      ctx.strokeStyle = '#bd7851'; ctx.lineWidth = 3; ctx.stroke();
      ctx.beginPath(); ctx.arc(px, roofY - 1, 2, 0, Math.PI * 2);
      ctx.fillStyle = '#ffe1a0'; ctx.fill();
    }
    ctx.restore();
  }
}
